import { useCallback, useEffect, useRef, useState } from 'react';
import { postAgentChat } from '../agent/api';
import { buildSystemPrompt } from '../agent/systemPrompt';
import { buildTranscript } from '../agent/transcript';
import { postAgentAction } from '../agent/api';
import { getOperatorName } from '../pages/reconcile/systemApi';
import { getPageContext } from '../agent/pageContext';
import {
  saveSessionSnapshot, listSessions, getSession, SessionSummary,
  openReservationSessionApi, fetchReservationTimeline, stripInjectedContext,
} from '../agent/sessionApi';
import { getSessionMeta, setSessionMeta, SessionMeta } from '../agent/sessionContext';
import { classifyMail, getWorkflow } from '../agent/mailWorkflows';
import {
  detectFalseSendClaim, detectStockClaim, detectStaffCorrection, detectSaveMismatch, detectFalseActionClaim,
  availabilityFromTool, sendEscalation, escalationNotice, type Escalation, type AvailabilityFact,
} from '../agent/contradiction';
import {
  AGENT_STEP_LABELS,
  getAgentToolDeclarations,
  dispatchTool,
  buildReservationSnapshot,
  runSendReservationEmail,
  discardAgentDraft,
  updateAgentDraftBody,
} from '../agent/tools';
import type {
  AgentStatus,
  AgentUiMessage,
  GeminiContent,
  PendingConfirm,
  WizardField,
  WizardState,
} from '../agent/types';
import { formatWizardAnswer } from '../agent/wizard';
import { applyAdvance, isWriteTool, normalizePlanArgs, planApprovedByOperator, planGuard } from '../agent/taskPlan';
import type { TaskPlan } from '../agent/taskPlan';
import { markDraftCardSent, SENT_BY_TOOL_NOTE } from '../agent/draftCards';
import {
  checkOnlyGuard, draftClaimGuard, findDraftClaimProblems, hasStaleNoToolClaim, isPaymentStatusRequest,
  paymentStatusHint, paymentStatusNudge, STALE_NO_TOOL_NOTE, AGENT_CLIENT_GUARDS, lastOperatorText, replyPolicyNudge, stalledPromiseNudge,
} from '../agent/requestIntent';
import {
  clearConversation,
  loadConversation,
  saveConversation,
  saveWizardDraft,
  loadWizardDraft,
  clearWizardDraft,
} from '../agent/storage';
import {
  planRecovery,
  withTransientRetry,
  AgentErrorActionId,
} from '../agent/errorRecovery';
import {
  recordAgentExecution,
  toResultStatus,
  summarizeToolResult,
  newSessionId,
} from '../agent/executionLog';

// 1ターン=1ツール呼び出しになりがちな Flash 系では 8 は少なすぎる。
// 2026-09-03: 予約確定の一巡（予約→在庫→メール一覧→本文→テンプレ→起案）が
// 8 で打ち切られ「処理が長時間化したため中断」になった（セッション602）
const MAX_LOOP = 12;

const uid = (() => {
  let n = 0;
  return () => `msg_${Date.now()}_${++n}`;
})();

const nowIso = () => new Date().toISOString();

function extractText(content: GeminiContent | undefined): string {
  if (!content) return '';
  return content.parts
    .map((p) => (typeof p.text === 'string' ? p.text : ''))
    .join('')
    .trim();
}

/**
 * functionCall.args が配列（[]）のとき {} に直す。
 * PHP プロキシの json 往復で {} が [] に化けることがあり（2026-08-25 の 400 事象）、
 * そのまま履歴に入れて送り返すと Gemini が
 * 「Unknown name "args" ... cannot start list」で全ターンを拒否する。
 * サーバ（agent_fix_contents）でも直しているが、履歴を汚さないよう受信時にも直す（二重の守り）。
 */
function normalizeFnCallArgs(p: any): any {
  if (p?.functionCall && Array.isArray(p.functionCall.args)) {
    return { ...p, functionCall: { ...p.functionCall, args: {} } };
  }
  return p;
}

/** テスト用エクスポート（実装は normalizeFnCallArgs） */
export const __test_normalizeFnCallArgs = (p: any) => normalizeFnCallArgs(p);

function extractFunctionCalls(content: GeminiContent | undefined) {
  if (!content) return [];
  return content.parts
    .filter((p) => p.functionCall && p.functionCall.name)
    .map((p) => normalizeFnCallArgs(p).functionCall!);
}

/**
 * Gemini の返答を contents に追加する前に、送信不能なパート（thought など）を除く。
 */
function sanitizeModelContent(content: GeminiContent): GeminiContent {
  return {
    role: content.role ?? 'model',
    parts: (content.parts || []).map(normalizeFnCallArgs).filter((p: any) => {
      if (p?.thought) return false;
      return p?.text || p?.functionCall || p?.functionResponse;
    }),
  };
}

/**
 * Gemini の turn 順序ルール:
 *   - functionCall を含む model turn の **直後** には functionResponse の user turn が必要
 *   - functionResponse の user turn の **直前** には functionCall の model turn が必要
 *
 * 上記を満たさない turn は drop する（末尾だけでなく中間にある孤立も除去）。
 * ウィザード/確認中にブラウザを閉じた localStorage 起因の破損状態を全クリーニングするため。
 */
function sanitizeContents(contents: GeminiContent[]): GeminiContent[] {
  if (!Array.isArray(contents) || contents.length === 0) return [];
  const valid: GeminiContent[] = [];
  for (let i = 0; i < contents.length; i++) {
    const cur = contents[i];
    if (!cur || !Array.isArray(cur.parts) || cur.parts.length === 0) continue;

    const hasFnCall = cur.role === 'model' && cur.parts.some((p: any) => p?.functionCall);
    const hasFnResp = cur.role === 'user' && cur.parts.some((p: any) => p?.functionResponse);

    if (hasFnCall) {
      const next = contents[i + 1];
      const nextHasResp = next?.role === 'user' && Array.isArray(next?.parts)
        && next.parts.some((p: any) => p?.functionResponse);
      if (!nextHasResp) continue; // 孤立 functionCall を捨てる
    }
    if (hasFnResp) {
      const prevValid = valid[valid.length - 1];
      const prevHasCall = prevValid?.role === 'model' && Array.isArray(prevValid?.parts)
        && prevValid.parts.some((p: any) => p?.functionCall);
      if (!prevHasCall) continue; // 直前に対応する call が無ければ捨てる
    }

    valid.push(cur);
  }
  return valid.map((c) => (c.role === 'model' ? sanitizeModelContent(c) : c));
}

// エラー分類・復帰プランは agent/errorRecovery.ts に集約（Track B, 2026-07-04）

export interface UseAgentReturn {
  uiMessages: AgentUiMessage[];
  status: AgentStatus;
  currentStep: string | null;
  pendingConfirm: PendingConfirm | null;
  wizard: WizardState | null;
  send: (text: string) => Promise<void>;
  resetConversation: () => void;
  resolveConfirm: (approved: boolean) => void;
  answerWizard: (value: any) => void;
  answerWizardAll: (values: Record<string, any>) => void;
  sendFeedback: (category: string, text: string) => Promise<boolean>;
  /** 3日分のセッション一覧（§16 S2） */
  listRecentSessions: (days?: number) => Promise<SessionSummary[]>;
  /** 保存済みセッションを読み込んで切り替える */
  loadSession: (id: number) => Promise<boolean>;
  /** アクティブなセッションの client_key（一覧でのハイライト用） */
  activeSessionKey: string;
  /** 予約セッションを開く（無ければ作る）。成功時 true。mailContext があれば起点メールを文脈に注入 */
  openReservationSession: (contactCode: string, label?: string,
    mailContext?: { sender?: string; subject?: string; snippet?: string; decision_id?: number; mail_id?: string }) => Promise<boolean>;
  /** 予約セッションから業務セッションへ戻る */
  returnToWorkSession: () => void;
  /** 現在のセッション種別（パンくず表示用） */
  sessionMeta: SessionMeta;
  /** エラーメッセージ下の復帰アクション（再試行 / リセット / コピー） */
  handleErrorAction: (action: AgentErrorActionId) => void;
  /** メール下書きカードの [送信]（2026-09-01。カードのボタン自体が人の承認） */
  sendMailDraft: (uiId: string, body: string) => Promise<void>;
  /** メール下書きカードの [破棄] */
  discardMailDraft: (uiId: string) => void;
  /** メール下書きカードの [編集] 保存（送信時に使う本文を差し替える） */
  editMailDraft: (uiId: string, body: string) => void;
}

export function useAgent(): UseAgentReturn {
  const [uiMessages, setUiMessages] = useState<AgentUiMessage[]>([]);
  const [status, setStatus] = useState<AgentStatus>('idle');
  const [currentStep, setCurrentStep] = useState<string | null>(null);
  const [pendingConfirm, setPendingConfirm] = useState<PendingConfirm | null>(null);
  const [wizard, setWizard] = useState<WizardState | null>(null);
  const wizardRef = useRef<WizardState | null>(null);
  useEffect(() => { wizardRef.current = wizard; }, [wizard]);

  // Gemini に送る contents（function call / response を含む完全履歴）
  const contentsRef = useRef<GeminiContent[]>([]);
  const busyRef = useRef(false);
  // turn 順序エラーの連続回数（1回目は切除で復旧、2回目で全リセット）
  const turnOrderStreakRef = useRef(0);
  // 実行ログの会話セッションID（Track A1。リセットで払い直す）
  const sessionIdRef = useRef(newSessionId());
  // dispatchTool のコールバックから最新の会話を読むための参照
  const uiMessagesRef = useRef<AgentUiMessage[]>([]);
  useEffect(() => { uiMessagesRef.current = uiMessages; }, [uiMessages]);

  // 永続化の復元
  useEffect(() => {
    const stored = loadConversation();
    if (stored) {
      setUiMessages(stored.uiMessages);
      contentsRef.current = sanitizeContents(stored.contents);
      // client_key を引き継ぐ（無いとリロードのたびにサーバに別セッションが増える）
      if (stored.clientKey) {
        sessionIdRef.current = stored.clientKey;
        setActiveSessionKey(stored.clientKey);
      }
      // セッション種別も復元（無いと予約セッションがリロードで業務扱いに戻り、
      // 誘導ルールが再注入され「開きますか？」と聞く・保存タイトルが上書きされる）
      if (stored.sessionMeta && stored.sessionMeta.kind === 'reservation' && stored.sessionMeta.anchor) {
        applySessionMeta(stored.sessionMeta);
      }
    }
  }, []);   // 初回マウント時のみ（applySessionMeta は実行時には定義済み）

  // 永続化の保存（idle / error 時のみ。処理途中は保存せず「クリーンな境界」だけ永続化）
  // サーバが正・localStorage はキャッシュ（§16-3）。サーバ保存は 1.5 秒 debounce。
  const serverSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (status !== 'idle' && status !== 'error') return;
    saveConversation({
      uiMessages, contents: contentsRef.current, clientKey: sessionIdRef.current,
      sessionMeta: sessionMetaRef.current,
    });
    if (serverSaveTimer.current) clearTimeout(serverSaveTimer.current);
    if (uiMessages.length === 0) return;
    serverSaveTimer.current = setTimeout(() => {
      const m = sessionMetaRef.current;
      void saveSessionSnapshot(sessionIdRef.current, uiMessagesRef.current, contentsRef.current,
        m.kind === 'reservation' ? { kind: 'reservation', anchor: m.anchor, title: m.label } : {});
    }, 1500);
    return () => { if (serverSaveTimer.current) clearTimeout(serverSaveTimer.current); };
  }, [uiMessages, status]);

  // セッション操作（§16 S2）
  const [activeSessionKey, setActiveSessionKey] = useState<string>('');
  useEffect(() => { setActiveSessionKey(sessionIdRef.current); }, []);

  const listRecentSessions = useCallback((days = 3) => listSessions(days), []);

  // 予約セッション（S3）。business → reservation の切替と復帰
  const [sessionMeta, setSessionMetaState] = useState<SessionMeta>({ kind: 'work' });
  const sessionMetaRef = useRef<SessionMeta>({ kind: 'work' });
  const applySessionMeta = useCallback((m: SessionMeta) => {
    sessionMetaRef.current = m;
    setSessionMeta(m);          // システムプロンプト・ツール解決用（モジュールストア）
    setSessionMetaState(m);     // パンくず表示用
  }, []);
  // 「戻る」用: 予約セッションへ入る直前の業務セッションのスナップショット（メモリ保持）
  const workSnapshotRef = useRef<{ clientKey: string; uiMessages: AgentUiMessage[]; contents: GeminiContent[] } | null>(null);

  const openReservationSession = useCallback(async (
    contactCode: string,
    label?: string,
    mailContext?: { sender?: string; subject?: string; snippet?: string; decision_id?: number; mail_id?: string },
  ): Promise<boolean> => {
    const code = contactCode.trim();
    if (!code) return false;
    // 既に同じ予約セッションを開いている場合は何もしない（mail-queue のボタン連打・
    // AI の open_reservation_session 再実行でタイムライン注入が重複するのを防ぐ）
    if (sessionMetaRef.current.kind === 'reservation' && sessionMetaRef.current.anchor === code) {
      return true;
    }
    const title = label || `予約 ${code}`;
    const opened = await openReservationSessionApi(code, title);
    if (!opened) return false;

    // 現行セッションを**必ず保存してから**切り替える。
    // 2026-08-30: 予約セッション → 別の予約セッションの切替時に保存が無く、
    // 未保存の対話が消えたり直後の debounce 保存が新セッション側へ紛れる余地があった
    if (uiMessagesRef.current.length > 0) {
      const cur = sessionMetaRef.current;
      void saveSessionSnapshot(sessionIdRef.current, uiMessagesRef.current, contentsRef.current,
        cur.kind === 'reservation'
          ? { kind: 'reservation', anchor: cur.anchor, title: cur.label }
          : undefined);
    }
    // 業務セッションからの切替なら退避（戻る用）
    if (sessionMetaRef.current.kind === 'work') {
      workSnapshotRef.current = {
        clientKey: sessionIdRef.current,
        uiMessages: uiMessagesRef.current,
        contents: contentsRef.current,
      };
    }

    sessionIdRef.current = opened.session.client_key;
    setActiveSessionKey(opened.session.client_key);
    // 起点メールの種別 → 標準手順（§16-6y）。件名・差出人で決定的に判定する
    let workflowKey = mailContext ? classifyMail(mailContext.subject || '', mailContext.sender || '') : null;
    applySessionMeta({ kind: 'reservation', anchor: code, label: opened.session.title || title,
      ...(workflowKey ? { workflow: workflowKey } : {}) });

    // 保存済みの対話を復元し、タイムライン（4系統マージ）を文脈として注入する。
    // 注入ブロック（📋/✉️ と【システム】contents）は transient — 保存対象にせず、
    // 復元時も過去の注入を捨てて**毎回最新を注入し直す**（積み上がり防止・2026-08-30）
    let contents: GeminiContent[] = [];
    try {
      const raw = (opened.session as any).contents_json;
      contents = stripInjectedContext(raw ? (JSON.parse(raw) || []) : []);
    } catch { contents = []; }
    // 古い画面のときの「◯◯を確認する道具がない」が履歴に残っていたら、今の道具で判断させる（2026-10-31 セッション3041）
    if (hasStaleNoToolClaim(contents)) {
      contents = [...contents, { role: 'user', parts: [{ text: STALE_NO_TOOL_NOTE }] } as GeminiContent];
    }
    // transient フラグ導入(2026-08-30)前に保存されてしまった注入ブロックも、
    // 先頭絵文字(📋/✉️ はこの注入だけが使う)で判別して落とす = 旧データのセルフヒーリング
    const ui = opened.uiMessages.filter((m: any) => !m.transient
      && !(m.kind === 'assistant' && /^(📋|✉️)/.test(String(m.text ?? ''))));
    // 予約スナップショット + タイムラインを並行取得（2026-09-03）。
    // スナップショットが無いと、セッション内なのに search_reservations を呼び直したり、
    // 日付を推測して check_schedule_conflict がエラーになる（記録#26 / セッション602）
    const [snapshot, tl] = await Promise.all([
      buildReservationSnapshot(code).catch(() => null),
      fetchReservationTimeline(code).catch(() => null),
    ]);
    if (snapshot) {
      ui.push({
        kind: 'assistant', id: `snap_${Date.now()}`, createdAt: new Date().toISOString(),
        text: `📄 この予約の現在の内容:\n${snapshot}`,
        transient: true,
      } as AgentUiMessage);
      contents = [...contents, { role: 'user', parts: [{ text:
        `【システム】この予約の現在の内容（開いた時点の実データ。search_reservations を`
        + `呼び直す必要はない。日付・料金・車両はここの値を使うこと）:\n${snapshot}` }] } as GeminiContent];
    }
    if (tl && tl.events.length > 0) {
      const recent = tl.events.slice(-15)
        .map((e) => `[${e.ts}] ${e.who}: ${e.text}`).join('\n');
      const block = `この予約のこれまでの作業履歴（メール・操作・メモの直近${Math.min(15, tl.events.length)}件 / 全${tl.count}件）:\n${recent}`;
      ui.push({
        kind: 'assistant', id: `tl_${Date.now()}`, createdAt: new Date().toISOString(),
        text: `📋 ${title} のセッションを開きました。\n\n${block}`,
        transient: true,   // 開くたびの注入。保存しない（積み上がり防止）
      } as AgentUiMessage);
      contents = [...contents, { role: 'user', parts: [{ text: `【システム】${block}` }] } as GeminiContent];
    }
    // メール以外（予約一覧・チャットのチップ等）から開いたときは、**直近の受信メール**で型を決める。
    // 2026-09-24 記録#32: 予約画面から開いた LP リクエストで手順が載らず、AI が定型テンプレを使わずに
    // 自由作文し、お客様向け本文に社内ステータス名「オファー中」を書いた。
    if (!mailContext && tl) {
      const lastIn = [...tl.events].reverse().find((e) => e.type === 'mail_in');
      const key = lastIn ? classifyMail(String(lastIn.text || '').replace(/^受信:\s*/, ''), lastIn.who || '') : null;
      const wf = getWorkflow(key);
      if (lastIn && wf) {
        workflowKey = key;
        applySessionMeta({ kind: 'reservation', anchor: code, label: opened.session.title || title, workflow: key! });
        ui.push({
          kind: 'assistant', id: `wf_${Date.now()}`, createdAt: new Date().toISOString(),
          text: `🧭 直近の受信メール（${lastIn.ts}「${String(lastIn.text).replace(/^受信:\s*/, '')}」）の型: **${wf.label}**\n`
            + `標準手順:\n${wf.procedure.map((p, i) => `${i + 1}. ${p}`).join('\n')}\n（下のチップで順に進められます）`,
          transient: true,
        } as AgentUiMessage);
        if (lastIn.mail_id) {
          contents = [...contents, { role: 'user', parts: [{ text:
            `【システム】直近の受信メール: mail_id="${lastIn.mail_id}" 差出人 ${lastIn.who} 件名「${String(lastIn.text).replace(/^受信:\s*/, '')}」。`
            + '「このメール」はこれを指す。返信可否・アーカイブは list_reservation_mails の repliable / mail_id に従う。' }] } as GeminiContent];
        }
      }
    }
    // 起点メールの文脈（mail-queue の「AIと対話」から来た場合）。
    // これがあると「このメール」がエージェントに通じ、返信起案が一言で済む
    if (mailContext && (mailContext.subject || mailContext.snippet)) {
      const mailBlock = `このセッションは次の受信メールから開かれました:\n`
        + `差出人: ${mailContext.sender || '不明'}\n件名: ${mailContext.subject || '(件名なし)'}\n`
        + (mailContext.snippet ? `抜粋: ${mailContext.snippet}` : '');
      const wf = getWorkflow(workflowKey);
      ui.push({
        kind: 'assistant', id: `mail_${Date.now()}`, createdAt: new Date().toISOString(),
        text: `✉️ ${mailBlock}` + (wf
          ? `\n\n🧭 メールの型: **${wf.label}**\n標準手順:\n${wf.procedure.map((p, i) => `${i + 1}. ${p}`).join('\n')}\n（下のチップで順に進められます）`
          : ''),
        transient: true,   // 開くたびの注入。保存しない（積み上がり防止）
      } as AgentUiMessage);
      // 自社システム通知（booking@n-car.jp 等の新規予約通知）には返信しない。
      // 2026-08-29 #172: LP通知へスレッド返信しようとし自社宛送信を3回試みた事象への対応
      const ownDomain = /@(n-car\.jp|n-car\.info|trailernchu\.co\.jp|pit-rent\.com)/i.test(mailContext.sender || '');
      const archiveHint = mailContext.mail_id
        ? `対応が完了したら archive_mail(mail_id: "${mailContext.mail_id}") でこのメールをアーカイブできる。`
        : '';
      const replyHint = ownDomain
        ? `\n⚠ このメールは**自社システムの通知**（差出人が自社ドメイン）。返信先にしてはいけない。お客様へ送るときは reply_to_mail_id を**渡さず**に起案する（お客様からの受信メールがあればサーバが自動でそのスレッドへの返信にする）。${archiveHint}`
        : mailContext.mail_id
          ? `\nこのメールへ返信するときは draft_reservation_email に reply_to_mail_id: "${mailContext.mail_id}" を渡すこと（スレッド返信になる。新規メールにしない）。${archiveHint}`
          : '';
      contents = [...contents, { role: 'user', parts: [{ text: `【システム】${mailBlock}\n「このメール」はこのメールを指す。${replyHint}` }] } as GeminiContent];
    }
    contentsRef.current = sanitizeContents(contents);
    setUiMessages(ui);
    setStatus('idle');
    setCurrentStep(null);
    setPendingConfirm(null);
    setWizard(null);
    activePlanRef.current = null;   // 読み込んだカードから currentPlan() が復元する
    return true;
  }, [applySessionMeta]);

  const returnToWorkSession = useCallback(() => {
    // 予約セッションの現状を保存してから戻る
    if (uiMessagesRef.current.length > 0) {
      void saveSessionSnapshot(sessionIdRef.current, uiMessagesRef.current, contentsRef.current, {
        kind: 'reservation', anchor: sessionMetaRef.current.anchor, title: sessionMetaRef.current.label,
      });
    }
    const snap = workSnapshotRef.current;
    workSnapshotRef.current = null;
    applySessionMeta({ kind: 'work' });
    if (snap) {
      sessionIdRef.current = snap.clientKey;
      setActiveSessionKey(snap.clientKey);
      contentsRef.current = snap.contents;
      setUiMessages(snap.uiMessages);
    } else {
      // リロード等で退避が無い場合は新規業務セッション
      sessionIdRef.current = newSessionId();
      setActiveSessionKey(sessionIdRef.current);
      contentsRef.current = [];
      setUiMessages([]);
    }
    setStatus('idle');
  }, [applySessionMeta]);

  const loadSession = useCallback(async (id: number): Promise<boolean> => {
    const loaded = await getSession(id);
    if (!loaded) return false;
    // 切替前に現行セッションを保存しておく（未保存分を失わない）
    if (uiMessagesRef.current.length > 0) {
      void saveSessionSnapshot(sessionIdRef.current, uiMessagesRef.current, contentsRef.current);
    }
    sessionIdRef.current = loaded.session.client_key;
    setActiveSessionKey(loaded.session.client_key);
    applySessionMeta(loaded.session.kind === 'reservation'
      ? { kind: 'reservation', anchor: loaded.session.anchor || '', label: loaded.session.title }
      : { kind: 'work' });
    contentsRef.current = sanitizeContents(loaded.contents);
    if (hasStaleNoToolClaim(contentsRef.current)) {
      contentsRef.current = [...contentsRef.current, { role: 'user', parts: [{ text: STALE_NO_TOOL_NOTE }] } as GeminiContent];
    }
    setUiMessages(loaded.uiMessages);
    setStatus('idle');
    setCurrentStep(null);
    setPendingConfirm(null);
    setWizard(null);
    return true;
  }, [applySessionMeta]);

  const appendUiMessage = useCallback((msg: AgentUiMessage) => {
    setUiMessages((prev) => [...prev, msg]);
  }, []);

  const updateUiMessage = useCallback((id: string, patch: Partial<AgentUiMessage>) => {
    setUiMessages((prev) =>
      prev.map((m) => (m.id === id ? ({ ...m, ...patch } as AgentUiMessage) : m)),
    );
  }, []);

  // ---- メール下書きカードの操作（2026-09-01 前田さん: 送信/編集/破棄の3ボタン）----
  // カードの [送信] が人の承認そのもの。押した結果は contents にも積んで AI に伝える
  // （AI が「送信済みなのにもう一度送ろうとする」「破棄されたのに再提案する」のを防ぐ）。
  const noteToModel = useCallback((text: string) => {
    contentsRef.current = [...contentsRef.current, { role: 'user', parts: [{ text }] } as GeminiContent];
  }, []);

  const sendMailDraft = useCallback(async (uiId: string, body: string) => {
    const card: any = uiMessagesRef.current.find((m) => m.id === uiId);
    if (!card || card.kind !== 'mail_draft' || card.state !== 'pending') return;
    setStatus('calling_tool');
    setCurrentStep('📧 メールを送信中…');
    const startedAt = Date.now();
    try {
      const res: any = await runSendReservationEmail({ draft_id: card.draftId, body });
      const durationMs = Date.now() - startedAt;
      recordAgentExecution({
        session_id: sessionIdRef.current, source: 'agent', tool_name: 'send_reservation_email',
        args: { draft_id: card.draftId, via: 'draft_card' },
        result_status: res?.success ? 'success' : 'error',
        result_summary: summarizeToolResult(res), reservation_id: null, duration_ms: durationMs,
      });
      if (res?.success) {
        updateUiMessage(uiId, { state: 'sent', body, note: `${res.sent_to} へ送信しました` } as any);
        noteToModel(`【システム】ユーザーが下書き ${card.draftId} の[送信]ボタンを押し、${res.sent_to} へ送信済みです。`
          + '同じ下書きを再送しないこと。');
      } else {
        updateUiMessage(uiId, { note: `⚠ 送信に失敗しました: ${res?.error || '不明なエラー'}` } as any);
        noteToModel(`【システム】下書き ${card.draftId} の送信に失敗しました: ${res?.error || '不明なエラー'}`);
      }
    } catch (e: any) {
      updateUiMessage(uiId, { note: `⚠ 送信に失敗しました: ${e?.message || e}` } as any);
    } finally {
      setStatus('idle');
      setCurrentStep(null);
    }
  }, [updateUiMessage, noteToModel]);

  const discardMailDraft = useCallback((uiId: string) => {
    const card: any = uiMessagesRef.current.find((m) => m.id === uiId);
    if (!card || card.kind !== 'mail_draft' || card.state !== 'pending') return;
    discardAgentDraft(card.draftId);
    updateUiMessage(uiId, { state: 'discarded', note: 'この下書きは送信しませんでした' } as any);
    noteToModel(`【システム】ユーザーが下書き ${card.draftId} を[破棄]しました。`
      + '同じ内容で再提案せず、どこを直すべきかユーザーに確認すること。');
  }, [updateUiMessage, noteToModel]);

  const editMailDraft = useCallback((uiId: string, body: string) => {
    const card: any = uiMessagesRef.current.find((m) => m.id === uiId);
    if (!card || card.kind !== 'mail_draft' || card.state !== 'pending') return;
    updateAgentDraftBody(card.draftId, body);
    updateUiMessage(uiId, { body, note: '（本文を編集しました。[送信]で編集後の内容が送られます）' } as any);
    noteToModel(`【システム】ユーザーが下書き ${card.draftId} の本文を手直ししました。編集後の本文:\n${body}`);
  }, [updateUiMessage, noteToModel]);

  const requestConfirm = useCallback(
    (args: Record<string, any>): Promise<boolean> => {
      return new Promise<boolean>((resolve) => {
        const confirmId = uid();
        appendUiMessage({
          kind: 'confirm',
          id: confirmId,
          createdAt: nowIso(),
          args,
          state: 'pending',
        });
        setStatus('awaiting_confirm');
        setPendingConfirm({
          args,
          resolve: (approved: boolean) => {
            updateUiMessage(confirmId, {
              state: approved ? 'approved' : 'rejected',
            } as any);
            setPendingConfirm(null);
            resolve(approved);
          },
        });
      });
    },
    [appendUiMessage, updateUiMessage],
  );

  const requestUpdateConfirm = useCallback(
    (
      before: Record<string, any>,
      changes: Array<{ key: string; label: string; before: string; after: string }>,
    ): Promise<boolean> => {
      return new Promise<boolean>((resolve) => {
        const confirmId = uid();
        appendUiMessage({
          kind: 'update_confirm',
          id: confirmId,
          createdAt: nowIso(),
          before,
          changes,
          state: 'pending',
        });
        setStatus('awaiting_confirm');
        setPendingConfirm({
          args: { _kind: 'update', before, changes },
          resolve: (approved: boolean) => {
            updateUiMessage(confirmId, {
              state: approved ? 'approved' : 'rejected',
            } as any);
            setPendingConfirm(null);
            resolve(approved);
          },
        });
      });
    },
    [appendUiMessage, updateUiMessage],
  );

  const resolveConfirm = useCallback(
    (approved: boolean) => {
      if (pendingConfirm) {
        pendingConfirm.resolve(approved);
      }
    },
    [pendingConfirm],
  );

  // ---- 手順カード（2026-10-30 前田さん: 完了までの全ステップを示してから1つずつ進める）----
  // いま有効な手順。ref は即時に読めるように持ち、会話を読み込み直したときはカードから復元する
  const activePlanRef = useRef<{ id: string; plan: TaskPlan; state: 'pending' | 'approved' } | null>(null);
  const currentPlan = useCallback(() => {
    if (activePlanRef.current) return activePlanRef.current;
    const card: any = [...uiMessagesRef.current].reverse().find((m) => m.kind === 'plan');
    if (card && card.state === 'approved') {
      activePlanRef.current = { id: card.id, plan: { title: card.title, steps: card.steps }, state: 'approved' };
    }
    return activePlanRef.current;
  }, []);

  const requestPlan = useCallback((args: Record<string, any>): Promise<any> => {
    const { plan, error } = normalizePlanArgs(args);
    if (!plan) return Promise.resolve({ success: false, error });
    // 出し直し: 古い手順カードは「差し替え」にする
    const prev = currentPlan();
    if (prev) updateUiMessage(prev.id, { state: 'superseded' } as any);
    // オペレータがチャットで請求書の取り消し・作成をはっきり指示したら、それを承認として扱う（2026-10-31 前田さん）。
    // 手順カードは出す（何をするかは見える）が、承認ボタンは待たない
    const lastUserText = String((([...uiMessagesRef.current].reverse().find((m: any) => m.kind === 'user')) as any)?.text ?? '');
    if (planApprovedByOperator(plan, lastUserText)) {
      const id = uid();
      const title = `${plan.title}（オペレータの指示で開始）`;
      activePlanRef.current = { id, plan: { ...plan, title }, state: 'approved' };
      appendUiMessage({ kind: 'plan', id, createdAt: nowIso(), title, steps: plan.steps, state: 'approved' });
      return Promise.resolve({ success: true, approved: true, approved_by: 'operator_instruction', steps: plan.steps.length,
        message: 'オペレータのチャットの指示を承認として手順を開始した。手順 1 から順に実行し、1つ終わるごとに advance_plan で印を付けてから次へ進むこと。' });
    }
    return new Promise((resolve) => {
      const id = uid();
      activePlanRef.current = { id, plan, state: 'pending' };
      appendUiMessage({ kind: 'plan', id, createdAt: nowIso(), title: plan.title, steps: plan.steps, state: 'pending' });
      setStatus('awaiting_confirm');
      setPendingConfirm({
        args: { _kind: 'plan', title: plan.title },
        resolve: (approved: boolean) => {
          updateUiMessage(id, { state: approved ? 'approved' : 'rejected' } as any);
          setPendingConfirm(null);
          if (approved) {
            activePlanRef.current = { id, plan, state: 'approved' };
            resolve({ success: true, approved: true, steps: plan.steps.length,
              message: '手順が承認された。手順 1 から順に実行し、1つ終わるごとに advance_plan で印を付けてから次へ進むこと。' });
          } else {
            activePlanRef.current = null;
            resolve({ success: true, approved: false,
              message: 'スタッフが手順を直したいと言っている。書き込みはせず、どこをどう直すかを聞いてから propose_plan を出し直すこと。' });
          }
        },
      });
    });
  }, [appendUiMessage, updateUiMessage, currentPlan]);

  const advancePlan = useCallback((args: Record<string, any>): any => {
    const cur = currentPlan();
    if (!cur || cur.state !== 'approved') {
      return { success: false, error: '承認済みの手順がありません。手順が要るなら先に propose_plan を呼んでください' };
    }
    const { plan, error, finished } = applyAdvance(cur.plan, args);
    if (!plan) return { success: false, error };
    updateUiMessage(cur.id, { steps: plan.steps, ...(finished ? { state: 'done' } : {}) } as any);
    activePlanRef.current = finished ? null : { ...cur, plan };
    const next = plan.steps.findIndex((st) => st.status === 'pending');
    return {
      success: true, step: Number(args.step), finished: !!finished,
      next_step: next >= 0 ? { number: next + 1, label: plan.steps[next].label, actor: plan.steps[next].actor } : null,
      ...(args.status === 'waiting_staff' ? { message: 'スタッフの操作待ち。次へ進まず、スタッフが「完了」と言ってから done にすること。' } : {}),
    };
  }, [updateUiMessage, currentPlan]);

  const requestWizard = useCallback(
    (fields: WizardField[]): Promise<Record<string, any>> => {
      if (!fields || fields.length === 0) {
        return Promise.resolve({});
      }
      // 中断されたウィザードのドラフト復元（B2）:
      // 同じフィールド構成（signature 一致）かつ 30 分以内なら回答を引き継ぐ
      const signature = fields.map((f) => f.key).join(',');
      const draft = loadWizardDraft();
      let initialAnswers: Record<string, any> = {};
      let startIndex = 0;
      if (draft && draft.signature === signature && Object.keys(draft.answers || {}).length > 0) {
        initialAnswers = { ...draft.answers };
        while (startIndex < fields.length && initialAnswers[fields[startIndex].key] !== undefined) {
          startIndex++;
        }
        appendUiMessage({
          kind: 'assistant',
          id: uid(),
          createdAt: nowIso(),
          text: `前回の入力（${Object.keys(initialAnswers).length}件）を復元しました。続きから入力してください。`,
        });
      }
      if (startIndex >= fields.length) {
        // 全項目が復元済み → ウィザードを出さずに即完了
        clearWizardDraft();
        return Promise.resolve(initialAnswers);
      }
      return new Promise<Record<string, any>>((resolve) => {
        const first = fields[startIndex];
        appendUiMessage({
          kind: 'assistant',
          id: uid(),
          createdAt: nowIso(),
          text: first.question,
        });
        setWizard({
          fields,
          currentIndex: startIndex,
          answers: initialAnswers,
          resolve,
        });
        setStatus('awaiting_wizard');
        setCurrentStep(null);
      });
    },
    [appendUiMessage],
  );

  const answerWizard = useCallback(
    (value: any) => {
      const current = wizardRef.current;
      if (!current) return;
      const field = current.fields[current.currentIndex];
      const skipped = value === null || value === '' || value === undefined;
      const newAnswers = { ...current.answers };
      if (!skipped) newAnswers[field.key] = value;

      appendUiMessage({
        kind: 'user',
        id: uid(),
        createdAt: nowIso(),
        text: formatWizardAnswer(field, skipped ? null : value),
      });

      // 回答のたびにドラフト保存（ブラウザを閉じても復元できる。B2）
      saveWizardDraft({
        signature: current.fields.map((f) => f.key).join(','),
        answers: newAnswers,
        savedAt: Date.now(),
      });

      const nextIndex = current.currentIndex + 1;
      if (nextIndex >= current.fields.length) {
        clearWizardDraft(); // 完了 → ドラフト破棄
        current.resolve(newAnswers);
        setWizard(null);
        setStatus('calling_tool');
      } else {
        const nextField = current.fields[nextIndex];
        appendUiMessage({
          kind: 'assistant',
          id: uid(),
          createdAt: nowIso(),
          text: nextField.question,
        });
        setWizard({ ...current, currentIndex: nextIndex, answers: newAnswers });
      }
    },
    [appendUiMessage],
  );

  /**
   * 残りの質問を**まとめて**回答する（2026-10-03 UI見直し: 予約作成10件でスタッフ入力142回のうち
   * 78回が「スキップ」・作成1件の中央値7分。1問ずつの往復をやめ、1枚のフォームで答えられるようにする）
   */
  const answerWizardAll = useCallback(
    (values: Record<string, any>) => {
      const current = wizardRef.current;
      if (!current) return;
      const remaining = current.fields.slice(current.currentIndex);
      const newAnswers = { ...current.answers };
      const summary: string[] = [];
      for (const f of remaining) {
        const v = values[f.key];
        const skipped = v === null || v === '' || v === undefined;
        if (f.required && skipped) return;   // 必須が空なら確定しない（フォーム側でも押せない）
        if (!skipped) {
          newAnswers[f.key] = v;
          summary.push(`${f.label ?? f.key}: ${formatWizardAnswer(f, v)}`);
        }
      }
      appendUiMessage({
        kind: 'user', id: uid(), createdAt: nowIso(),
        text: `（まとめて入力）${summary.length ? summary.join(' / ') : '追加情報なし'}`,
      });
      clearWizardDraft();
      current.resolve(newAnswers);
      setWizard(null);
      setStatus('calling_tool');
    },
    [appendUiMessage],
  );

  const resetConversation = useCallback(() => {
    if (pendingConfirm) {
      pendingConfirm.resolve(false);
    }
    if (wizardRef.current) {
      wizardRef.current.resolve({});
    }
    setPendingConfirm(null);
    setWizard(null);
    setCurrentStep(null);
    activePlanRef.current = null;
    // 新規セッションへ移る前に、現行の未保存分をサーバへ残す（§16 S2）
    if (uiMessagesRef.current.length > 0) {
      void saveSessionSnapshot(sessionIdRef.current, uiMessagesRef.current, contentsRef.current);
    }
    contentsRef.current = [];
    turnOrderStreakRef.current = 0;
    sessionIdRef.current = newSessionId(); // 新しい会話セッション
    setActiveSessionKey(sessionIdRef.current);
    applySessionMeta({ kind: 'work' });
    setUiMessages([]);
    setStatus('idle');
    clearConversation();
    clearWizardDraft(); // 明示的なリセットではドラフトも破棄
  }, [pendingConfirm]);

  /** 矛盾をスタッフ全員へメールし、結果をチャットに一行出す（2026-10-03。送れなくても黙らない） */
  const escalate = useCallback((esc: Escalation) => {
    void sendEscalation(esc, { sessionId: sessionIdRef.current, uiMessages: uiMessagesRef.current })
      .then((r) => appendUiMessage({ kind: 'assistant', id: uid(), createdAt: nowIso(), text: escalationNotice(esc, r) }));
  }, [appendUiMessage]);

  /** 最新の下書きカードが未送信か（古い下書きの取り残しでは判定しない） */
  /**
   * 「フィードバックを送る」（2026-10-03）: スタッフ全員へメール（agent_escalations）＋ 会話記録（get_conversations）。
   * どちらか片方でも残れば成功扱い。結果はチャットに一行出す
   */
  const sendFeedback = useCallback(async (category: string, text: string): Promise<boolean> => {
    const transcript = buildTranscript(uiMessagesRef.current);
    const esc: Escalation = {
      kind: 'staff_feedback',
      title: `${category}: ${text.replace(/\s+/g, ' ').slice(0, 80)}`,
      evidence: [`スタッフのフィードバック（${category}）: ${text}`],
    };
    const [mail, record] = await Promise.all([
      sendEscalation(esc, { sessionId: sessionIdRef.current, uiMessages: uiMessagesRef.current }),
      transcript.trim()
        ? postAgentAction('log_conversation', {
            session_id: sessionIdRef.current, operator: getOperatorName() || 'unknown',
            note: `[フィードバック/${category}] ${text}`, page: getPageContext().page, transcript,
            message_count: transcript.split('\n\n').length,
          }).catch((e: any) => ({ success: false, error: e?.message || String(e) }))
        : Promise.resolve({ success: false, error: '会話がまだありません' }),
    ]);
    const recordId = (record as any)?.success ? (record as any).id : null;
    appendUiMessage({
      kind: 'assistant', id: uid(), createdAt: nowIso(),
      text: escalationNotice(esc, mail) + (recordId ? `（会話記録 ${recordId}）` : ''),
    });
    return mail.ok || !!recordId;
  }, [appendUiMessage]);

  const latestDraftPending = useCallback((): boolean => {
    const drafts = uiMessagesRef.current.filter((m: any) => m.kind === 'mail_draft');
    const last: any = drafts[drafts.length - 1];
    return !!last && (last.state ?? 'pending') === 'pending';
  }, []);

  const runLoop = useCallback(async () => {
    const systemInstruction = buildSystemPrompt();
    let lastAvailability: AvailabilityFact | null = null;   // このターンで最後に見た在庫の事実
    let toolsSucceeded = 0;          // このターン（ユーザー発言1回分）で成功したツールの数
    let actionClaimNudged = false;   // うその完了報告への差し戻しは1ターン1回まで
    let paymentNudged = false;       // 決済の確認を Square を見ずに答えたときの差し戻しも1ターン1回まで
    const succeededTools: string[] = [];   // このターンで成功したツール（決済を確かめたか）
    const writesThisTurn: string[] = [];   // このターンで成功した書き込み（手順カードの要否を見る）
    // オペレータの最後の発言。画面の一覧（uiMessagesRef）は描画のあとに追いつくので、送った直後のここで読むと
    // 1つ前の発言になる（2026-10-31 セッション3041）。送る前に積む会話の履歴から取る
    const lastUserText = lastOperatorText(contentsRef.current)
      || String((([...uiMessagesRef.current].reverse().find((m: any) => m.kind === 'user')) as any)?.text ?? '');
    let policyNudged = false;        // 方針の言い切りへの差し戻しも1ターン1回まで
    let stallNudged = false;         // 「〜します」で止まったときの差し戻しも1ターン1回まで
    // この会話のループであることを覚えておく。途中で新しい会話・別セッションへ切り替わったら、
    // 戻ってきた応答・ツール結果を**新しい会話に書き込まずに捨てる**（2026-10-03: 予約作成ウィザードの
    // 途中で新規会話にすると、中断した create_reservation の結果が新しい会話の履歴に入り
    // 「会話履歴に不整合」が7日で6回出ていた）
    const loopSession = sessionIdRef.current;
    const stale = () => sessionIdRef.current !== loopSession;

    for (let i = 0; i < MAX_LOOP; i++) {
      setStatus('thinking');
      setCurrentStep('🤖 考え中…');

      // 例外 / success:false を単一のエラーパスに統一し、
      // transient エラー（サンプリング揺らぎ・一時的過負荷）はバックオフ付きで最大3回試行（B4）
      const callOnce = async () => {
        const r = await postAgentChat({
          system_instruction: systemInstruction,
          contents: contentsRef.current,
          tools: [{ functionDeclarations: getAgentToolDeclarations() }],
          rule_scope: 'agent', // DB の追加ルールをサーバー側で注入（Track A2）
          client_guards: AGENT_CLIENT_GUARDS,   // 判定を持たない古い画面はサーバが断る
        });
        if (!r.success || !r.content) {
          throw new Error(String(r.error || 'エージェントエラー: 不明なエラー'));
        }
        return r;
      };

      let response;
      try {
        response = await withTransientRetry(callOnce, {
          retries: 2,
          onRetry: (attempt, msg) => {
            // eslint-disable-next-line no-console
            console.warn(`[agent] transient Gemini error → retry ${attempt}/2`, msg);
          },
        });
        turnOrderStreakRef.current = 0;
      } catch (e: any) {
        if (stale()) return;
        const msg = String(e?.message || e);
        // エラー種別 → 履歴の扱い + 提示アクションは errorRecovery が宣言的に決める（B1/B2）
        const plan = planRecovery(msg, { consecutiveTurnOrderErrors: turnOrderStreakRef.current });
        turnOrderStreakRef.current = plan.kind === 'turn_order' ? turnOrderStreakRef.current + 1 : 0;
        if (plan.history === 'clear') {
          contentsRef.current = [];
          clearConversation();
        } else if (plan.history === 'prune') {
          // 壊れたターンだけ切除して履歴を残す（従来の全消しから緩和）
          contentsRef.current = sanitizeContents(contentsRef.current);
        }
        appendUiMessage({
          kind: 'error',
          id: uid(),
          createdAt: nowIso(),
          text: plan.userText,
          actions: plan.actions,
        });
        setStatus('error');
        setCurrentStep(null);
        return;
      }

      if (stale()) return;
      const modelContent = sanitizeModelContent(response.content!);
      if (modelContent.parts.length > 0) {
        contentsRef.current = [...contentsRef.current, modelContent];
      }

      const calls = extractFunctionCalls(modelContent);
      const text = extractText(modelContent);

      if (text) {
        appendUiMessage({
          kind: 'assistant',
          id: uid(),
          createdAt: nowIso(),
          text,
        });
        // AI の発言と事実の矛盾（送信の誤報・在庫の誤報）
        const claim = detectFalseSendClaim(text, latestDraftPending() ? 1 : 0, 0)
          ?? detectStockClaim(text, lastAvailability);
        if (claim) escalate(claim);
      }

      // 決済の確認依頼に Square を見ずに答えた・確認したと言った → 1回だけ get_square_payment_status を呼ばせる
      // （2026-10-31 セッション3041: 5回頼まれて一度も呼ばず、「確認いたしました」とも言った）
      if (calls.length === 0 && text && !paymentNudged) {
        const nudge = paymentStatusNudge(text, lastUserText, succeededTools);
        if (nudge) {
          paymentNudged = true;
          appendUiMessage({ kind: 'assistant', id: uid(), createdAt: nowIso(),
            text: '⚠ まだ Square の決済状況を確認していません。確認します…' });
          contentsRef.current = [...contentsRef.current, { role: 'user', parts: [{ text: nudge }] } as GeminiContent];
          continue;
        }
      }

      // 「計算します・少々お待ちください」と言ってツールを呼ばずに終えた → 1回だけ実行させる（2026-11-03 N3）
      if (calls.length === 0 && text && !stallNudged) {
        const nudge = stalledPromiseNudge(text);
        if (nudge) {
          stallNudged = true;
          contentsRef.current = [...contentsRef.current, { role: 'user', parts: [{ text: nudge }] } as GeminiContent];
          continue;
        }
      }

      // 店の方針（事前決済の原則・現地払い不可など）を、オペレータが言っていないのに言い切った → 1回だけ聞き直させる
      if (calls.length === 0 && text && !policyNudged) {
        const nudge = replyPolicyNudge(text, lastUserText);
        if (nudge) {
          policyNudged = true;
          appendUiMessage({ kind: 'assistant', id: uid(), createdAt: nowIso(),
            text: '⚠ 直前の返答の「店の方針」は、オペレータの指示ではありません。言い直させます…' });
          contentsRef.current = [...contentsRef.current, { role: 'user', parts: [{ text: nudge }] } as GeminiContent];
          continue;
        }
      }

      // うその完了報告（ツールを呼ばずに「記入しました」）: 1回だけ AI に差し戻して、実際に操作するか訂正させる。
      // それでも直らなければスタッフ全員へ（2026-10-16 セッション2379）
      if (calls.length === 0 && text) {
        const falseAction = detectFalseActionClaim(text, toolsSucceeded, lastUserText);
        if (falseAction && !actionClaimNudged) {
          actionClaimNudged = true;
          appendUiMessage({ kind: 'assistant', id: uid(), createdAt: nowIso(),
            text: '⚠ 直前の返答の操作は、まだ実行されていません（ツールの実行記録がありません）。実行し直します…' });
          contentsRef.current = [...contentsRef.current, { role: 'user', parts: [{ text:
            '【システム】直前の返答は予約データを変えたと言っているが、このターンでツールを1つも実行していない。'
            + '操作が必要ならいま該当のツールを呼ぶこと。呼べない・やめるなら「まだ実行していない」とはっきり訂正すること。' }] } as GeminiContent];
          continue;
        }
        if (falseAction && actionClaimNudged) escalate(falseAction);
      }

      if (calls.length === 0) {
        setStatus('idle');
        setCurrentStep(null);
        return;
      }

      // tool 呼び出しを順に実行し、結果を1つの content としてまとめる
      const responseParts: any[] = [];
      for (const call of calls) {
        const label = AGENT_STEP_LABELS[call.name]?.running || `🛠️ ${call.name} を実行中…`;
        setStatus('calling_tool');
        setCurrentStep(label);

        const activityId = uid();
        appendUiMessage({
          kind: 'activity',
          id: activityId,
          createdAt: nowIso(),
          toolName: call.name,
          args: call.args || {},
          status: 'running',
        });

        // 承認済みの手順が無いまま2つ目の書き込みに進もうとしたら止める（taskPlan.planGuard）
        const plan = currentPlan();
        const blocked = planGuard(call.name, writesThisTurn, plan?.state === 'approved', lastUserText);
        if (blocked) {
          updateUiMessage(activityId, { status: 'error', errorMessage: '手順の一覧が先です（propose_plan）', durationMs: 0 } as any);
          responseParts.push({ functionResponse: { name: call.name, response: { result: { success: false, plan_required: true, error: blocked } } } });
          continue;
        }
        // 確認だけの依頼で状態を変えない・お客様向けの文に事実でないこと／決めていない方針を書かない（2026-10-31 セッション3041）
        const invoiceCreated = uiMessagesRef.current.some((m: any) => m.kind === 'activity'
          && m.toolName === 'create_square_invoice' && m.result?.success === true);
        const intentBlocked = checkOnlyGuard(call.name, lastUserText)
          ?? draftClaimGuard(call.name, call.args || {}, { invoiceCreated, lastUserText });
        if (intentBlocked) {
          updateUiMessage(activityId, { status: 'error', errorMessage: '実行していません（確認だけの依頼／書けない内容）', durationMs: 0 } as any);
          responseParts.push({ functionResponse: { name: call.name, response: { result: { success: false, not_executed: true, error: intentBlocked } } } });
          continue;
        }

        // 決済の確認でサーバが最初の1手を固定したとき、予約番号を省くことがある → 予約セッションの番号で補う
        if (call.name === 'get_square_payment_status' && !String((call.args as any)?.contact_code ?? '').trim()) {
          const meta = getSessionMeta();
          const anchor = meta.kind === 'reservation' ? String((meta as any).anchor ?? '').trim() : '';
          if (anchor) call.args = { ...(Array.isArray(call.args) ? {} : (call.args || {})), contact_code: anchor };
        }

        const startedAt = Date.now();
        try {
          const result = await dispatchTool(call.name, call.args || {}, {
            requestConfirm, requestUpdateConfirm, requestWizard, requestPlan, advancePlan,
            // 請求書の取り消し・作成: オペレータの指示を承認として扱うかの判定に使う
            lastUserText,
            // record_conversation 用: 画面に出ているやりとりをそのまま渡す
            sessionId: sessionIdRef.current,
            getTranscript: () => buildTranscript(uiMessagesRef.current),
          });
          if (stale()) return;
          const durationMs = Date.now() - startedAt;
          lastAvailability = availabilityFromTool(call.name, result) ?? lastAvailability;
          // 指示文（instruction）からサーバが作文した下書きも、出来上がった本文で同じ確認をする
          if (call.name === 'draft_reservation_email' && (result as any)?.success && (result as any)?.body) {
            const problems = [...((result as any).claim_problems ?? []), ...findDraftClaimProblems(String((result as any).body), {
              invoiceCreated: uiMessagesRef.current.some((m: any) => m.kind === 'activity'
                && m.toolName === 'create_square_invoice' && m.result?.success === true),
              lastUserText,
            })];
            if (problems.length) {
              (result as any).claim_problems = problems;
              (result as any).message = `⚠ この下書きには直すところがあります: ${problems.join('。')}。オペレータにそう伝え、確認してから起案し直すこと（この下書きは送らない）。`;
              appendUiMessage({ kind: 'assistant', id: uid(), createdAt: nowIso(),
                text: `⚠ 下書き #${(result as any).draft_id ?? ''} は送らないでください: ${problems.join('。')}` });
            }
          }
          if (!(result && typeof result === 'object' && (result as any).success === false)) {
            toolsSucceeded += 1;
            succeededTools.push(call.name);
            if (isWriteTool(call.name) && !(result as any)?.cancelled) writesThisTurn.push(call.name);
          }
          const mismatch = detectSaveMismatch(call.name, result);
          if (mismatch) escalate(mismatch);
          updateUiMessage(activityId, {
            status: 'success',
            result,
            durationMs,
          } as any);
          // AI がツールで送れたら、その下書きのカードも送信済みにする（2026-11-03 A9: カードが未送信のまま残り、
          // AI が「カードの［送信］で送られます」と答えていた。二重送信の恐れ）
          if (call.name === 'send_reservation_email' && (result as any)?.success) {
            const sentId = Number((call.args as any)?.draft_id);
            if (Number.isFinite(sentId)) {
              setUiMessages((prev) => markDraftCardSent(prev, sentId, String((result as any).sent_to ?? '')));
              (result as any).message = `${String((result as any).message ?? '送信しました')}。${SENT_BY_TOOL_NOTE}`;
            }
          }
          // 起案が成功したら**下書きカード**をチャットに出す（2026-09-01）。
          // 宛先・件名・本文を改行つきで見せ、[送信][編集][破棄] で人が決める
          if (call.name === 'draft_reservation_email' && (result as any)?.success && (result as any)?.draft_id) {
            const r: any = result;
            appendUiMessage({
              kind: 'mail_draft',
              id: uid(),
              createdAt: nowIso(),
              draftId: Number(r.draft_id),
              to: String(r.send_to ?? ''),
              subject: String(r.subject ?? ''),
              body: String(r.body ?? ''),
              replyMode: r.reply_mode === 'thread' ? 'thread' : 'new',
              state: 'pending',
            });
          }
          // 実行ログを永続化（fire-and-forget。Track A1）
          recordAgentExecution({
            session_id: sessionIdRef.current,
            source: 'agent',
            tool_name: call.name,
            args: call.args || {},
            result_status: toResultStatus(result),
            result_summary: summarizeToolResult(result),
            reservation_id: result?.reservation_id ?? (call.args as any)?.reservation_id ?? null,
            duration_ms: durationMs,
          });
          responseParts.push({
            functionResponse: {
              name: call.name,
              response: { result },
            },
          });
        } catch (e: any) {
          if (stale()) return;
          const durationMs = Date.now() - startedAt;
          const message = e?.message || String(e);
          updateUiMessage(activityId, {
            status: 'error',
            errorMessage: message,
            durationMs,
          } as any);
          recordAgentExecution({
            session_id: sessionIdRef.current,
            source: 'agent',
            tool_name: call.name,
            args: call.args || {},
            result_status: 'error',
            result_summary: message,
            reservation_id: (call.args as any)?.reservation_id ?? null,
            duration_ms: durationMs,
          });
          responseParts.push({
            functionResponse: {
              name: call.name,
              response: { error: message },
            },
          });
        }
      }

      contentsRef.current = [
        ...contentsRef.current,
        { role: 'user', parts: responseParts },
      ];
      // loop 継続
    }

    appendUiMessage({
      kind: 'error',
      id: uid(),
      createdAt: nowIso(),
      text: `処理が長時間化したため中断しました（最大${MAX_LOOP}ステップ）`,
    });
    setStatus('error');
    setCurrentStep(null);
  }, [appendUiMessage, requestConfirm, requestUpdateConfirm, requestWizard, requestPlan, advancePlan, currentPlan,
    updateUiMessage, escalate, latestDraftPending]);

  /** 直前の状態から会話を再開する（エラーアクション「再試行」。B2） */
  const retryLast = useCallback(async () => {
    if (busyRef.current) return;
    if (contentsRef.current.length === 0) return;
    busyRef.current = true;
    try {
      await runLoop();
    } finally {
      busyRef.current = false;
    }
  }, [runLoop]);

  /** エラーメッセージ下の復帰アクション（B2） */
  const handleErrorAction = useCallback(
    (action: AgentErrorActionId) => {
      if (action === 'retry') {
        void retryLast();
        return;
      }
      if (action === 'reset') {
        resetConversation();
        return;
      }
      if (action === 'copy') {
        // スタッフへの引き継ぎ用に直近のやり取りをコピー
        const lines = uiMessages.slice(-20).map((m) => {
          if (m.kind === 'user') return `[お客様対応スタッフ] ${m.text}`;
          if (m.kind === 'assistant') return `[AI] ${m.text}`;
          if (m.kind === 'error') return `[エラー] ${m.text}`;
          if (m.kind === 'activity') return `[実行] ${m.toolName} → ${m.status}`;
          return '';
        }).filter(Boolean);
        try {
          void navigator.clipboard?.writeText(`■エージェント会話ログ（引き継ぎ）\n${lines.join('\n')}`);
        } catch { /* ignore */ }
      }
    },
    [retryLast, resetConversation, uiMessages],
  );

  const send = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed) return;
      if (busyRef.current) return;
      busyRef.current = true;

      const hadAssistant = uiMessagesRef.current.some((m: any) => m.kind === 'assistant' && !m.transient);
      appendUiMessage({
        kind: 'user',
        id: uid(),
        createdAt: nowIso(),
        text: trimmed,
      });
      // スタッフが AI の誤りを指摘した → 全員へ（前田さんが開発チャットでフィードバックする運用）
      const correction = detectStaffCorrection(trimmed, hadAssistant);
      if (correction) escalate(correction);

      // 破損状態のリカバリ: 末尾に孤立 functionCall があると Gemini に拒否されるので除く
      contentsRef.current = sanitizeContents(contentsRef.current);
      // 未送信の下書きカードの事実を添える（2026-09-30 セッション1547: 「完了」に対し、
      // 送信の通知が無いのに「メールの送信が完了しました」と答えた）。【システム】は保存時に落ちる
      const pendingDrafts = uiMessagesRef.current
        .filter((m: any) => m.kind === 'mail_draft' && (m.state ?? 'pending') === 'pending')
        .map((m: any) => Number(m.draftId))
        .filter((n) => Number.isFinite(n));
      const parts: any[] = [{ text: trimmed }];
      // 決済の確認依頼なら、最初に get_square_payment_status を呼ぶよう添える（2026-10-31 セッション3041）
      if (isPaymentStatusRequest(trimmed)) {
        const meta = getSessionMeta();
        parts.push({ text: paymentStatusHint(meta.kind === 'reservation' ? String((meta as any).anchor ?? '') : '') });
      }
      if (pendingDrafts.length > 0) {
        parts.push({ text: `【システム】カードの状態: 未送信の下書き ${pendingDrafts.map((n) => `#${n}`).join(' ')}`
          + `（最新は #${pendingDrafts[pendingDrafts.length - 1]}）。送信は人がカードの［送信］で行う。`
          + '送信済みの通知が無い下書きを「送信しました」と言わない。' });
      }
      contentsRef.current = [
        ...contentsRef.current,
        { role: 'user', parts },
      ];

      try {
        await runLoop();
      } finally {
        busyRef.current = false;
      }
    },
    [appendUiMessage, runLoop, escalate],
  );

  return {
    uiMessages,
    status,
    currentStep,
    pendingConfirm,
    wizard,
    send,
    resetConversation,
    resolveConfirm,
    answerWizard,
    answerWizardAll,
    sendFeedback,
    handleErrorAction,
    listRecentSessions,
    loadSession,
    activeSessionKey,
    openReservationSession,
    returnToWorkSession,
    sessionMeta,
    sendMailDraft,
    discardMailDraft,
    editMailDraft,
  };
}
